import {
  selectGroupModelProfile,
  useGroupModelProfilesData,
} from "@/data/use-group-model-profiles-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faFolder,
  faFolders,
  faGlobe,
  faHeadSide,
  faImage,
  faPaperclip,
  faStop,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect } from "react";
import { Capabilities } from "../Assist/AssistContext";
import { ToggleButton } from "../ToggleButton/ToggleButton";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Row } from "../ui/flex";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTriggerWrapper,
} from "../ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export interface AIChatInputActionsProps {
  // Expert selection
  showExpertSelection?: boolean;
  isForwardingTranscript?: boolean;

  // File attachment
  onFileSelect?: () => void;
  filePickerElement?: React.ReactNode;
  selectedFolders: null | number;
  onToggleFolderSelection: () => void;

  // Submit and cancel
  onSubmit?: () => void;
  onCancel?: () => void;
  isPending?: boolean;
  isSubmitDisabled?: boolean;
  disabled?: boolean;

  // Context
  capabilities?: Capabilities;
  onCapabilitiesChange?: (capabilities: Capabilities) => void;
  modelProfileUuid?: string;
  onModelProfileUuidChange?: (uuid: string) => void;
}

export const AIChatInputActions: React.FC<AIChatInputActionsProps> = ({
  onFileSelect,
  filePickerElement,
  selectedFolders,
  onToggleFolderSelection,
  onSubmit,
  onCancel,
  isPending = false,
  isSubmitDisabled = false,
  disabled = false,
  capabilities,
  onCapabilitiesChange,
  modelProfileUuid,
  onModelProfileUuidChange,
}) => {
  const groupUuid = useGroupUuid();
  const { modelProfiles, isLoadingModelProfiles } =
    useGroupModelProfilesData(groupUuid);
  const selectedModel = selectGroupModelProfile(
    modelProfiles,
    modelProfileUuid,
  );

  useEffect(() => {
    if (
      isLoadingModelProfiles ||
      !selectedModel ||
      selectedModel.uuid === modelProfileUuid
    ) {
      return;
    }
    onModelProfileUuidChange?.(selectedModel.uuid);
  }, [
    isLoadingModelProfiles,
    modelProfileUuid,
    onModelProfileUuidChange,
    selectedModel,
  ]);

  if (!groupUuid) {
    return null;
  }

  return (
    <Row justify="end" align="center" className="h-8 gap-3">
      <Row className="items-center gap-1">
        {selectedFolders !== null && (
          <Button
            size="icon"
            variant="ghost"
            onClick={onToggleFolderSelection}
            className="relative"
            disabled={isPending}
            aria-label="Add Folders"
            title="Add Folders"
          >
            {selectedFolders > 0 && (
              <Badge className="absolute top-0 right-0 h-[14px] min-w-[14px] px-0.5 text-[10px]">
                {selectedFolders}
              </Badge>
            )}
            <FontAwesomeIcon icon={faFolders} />
          </Button>
        )}
        {filePickerElement ??
          (onFileSelect && (
            <Button
              size="icon"
              variant="ghost"
              onClick={onFileSelect}
              disabled={disabled || isPending}
              aria-label="Add Files"
              title="Add Files"
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </Button>
          ))}

        {capabilities !== undefined && (
          <>
            <div className="bg-background-4 flex flex-row items-center gap-0 rounded-full px-1">
              <Tooltip>
                <TooltipContent>
                  {capabilities.webSearch
                    ? "Disable web search"
                    : "Enable web search"}
                </TooltipContent>
                <TooltipTrigger asChild>
                  <ToggleButton
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isPending}
                    active={capabilities.webSearch}
                    aria-label={
                      capabilities.webSearch
                        ? "Disable web search"
                        : "Enable web search"
                    }
                    title={
                      capabilities.webSearch
                        ? "Disable web search"
                        : "Enable web search"
                    }
                    onClick={() => {
                      onCapabilitiesChange?.({
                        ...capabilities,
                        webSearch: !capabilities.webSearch,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faGlobe} />
                  </ToggleButton>
                </TooltipTrigger>
              </Tooltip>

              <Tooltip>
                <TooltipContent>
                  {capabilities.projectFiles
                    ? "Disable workbook files"
                    : "Enable workbook files"}
                </TooltipContent>
                <TooltipTrigger asChild>
                  <ToggleButton
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isPending}
                    active={capabilities.projectFiles}
                    aria-label={
                      capabilities.projectFiles
                        ? "Disable workbook files"
                        : "Enable workbook files"
                    }
                    title={
                      capabilities.projectFiles
                        ? "Disable workbook files"
                        : "Enable workbook files"
                    }
                    onClick={() => {
                      onCapabilitiesChange?.({
                        ...capabilities,
                        projectFiles: !capabilities.projectFiles,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faFolder} />
                  </ToggleButton>
                </TooltipTrigger>
              </Tooltip>

              <Tooltip>
                <TooltipContent>
                  {capabilities.expertFiles
                    ? "Disable expert files"
                    : "Enable expert files"}
                </TooltipContent>
                <TooltipTrigger asChild>
                  <ToggleButton
                    size="icon"
                    variant="ghost"
                    disabled={disabled || isPending}
                    active={capabilities.expertFiles}
                    aria-label={
                      capabilities.expertFiles
                        ? "Disable expert files"
                        : "Enable expert files"
                    }
                    title={
                      capabilities.expertFiles
                        ? "Disable expert files"
                        : "Enable expert files"
                    }
                    onClick={() => {
                      onCapabilitiesChange?.({
                        ...capabilities,
                        expertFiles: !capabilities.expertFiles,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faHeadSide} />
                  </ToggleButton>
                </TooltipTrigger>
              </Tooltip>
            </div>

            <Tooltip>
              <TooltipContent>
                {capabilities.imageGeneration
                  ? "Disable Image Generation"
                  : "Enable Image Generation"}
              </TooltipContent>
              <TooltipTrigger asChild>
                <ToggleButton
                  size="icon"
                  variant="ghost"
                  disabled={disabled || isPending}
                  active={capabilities.imageGeneration}
                  aria-label={
                    capabilities.imageGeneration
                      ? "Disable Image Generation"
                      : "Enable Image Generation"
                  }
                  title={
                    capabilities.imageGeneration
                      ? "Disable Image Generation"
                      : "Enable Image Generation"
                  }
                  onClick={() => {
                    onCapabilitiesChange?.({
                      ...capabilities,
                      imageGeneration: !capabilities.imageGeneration,
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faImage} />
                </ToggleButton>
              </TooltipTrigger>
            </Tooltip>
          </>
        )}

        {selectedModel !== undefined &&
          onModelProfileUuidChange !== undefined && (
            <Select
              value={selectedModel.uuid}
              onValueChange={(v) => onModelProfileUuidChange(v as string)}
              disabled={disabled || isPending}
            >
              <SelectTriggerWrapper asChild>
                <Button
                  variant="outline"
                  className="text-xs"
                  aria-label="Select Model"
                  title="Select Model"
                >
                  {selectedModel.label}
                </Button>
              </SelectTriggerWrapper>
              <SelectContent align="start">
                {modelProfiles.map(({ uuid, textModelSettings, label }) => (
                  <SelectItem
                    key={uuid}
                    value={uuid}
                    className="flex flex-col items-start justify-center gap-1"
                  >
                    <div className="w-16">{label}</div>
                    <div>{textModelSettings.model}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
      </Row>

      {(onSubmit || onCancel) && (
        <Tooltip>
          <TooltipContent>
            {isPending
              ? "Cancel message"
              : isSubmitDisabled
                ? "Write message and select expert"
                : "Submit message"}
          </TooltipContent>
          <TooltipTrigger>
            <Button
              size="icon"
              onClick={isPending ? onCancel : onSubmit}
              disabled={isPending ? !onCancel : isSubmitDisabled}
              data-testid="ai-chat-submit-button"
              aria-label={
                isPending
                  ? "Cancel message"
                  : isSubmitDisabled
                    ? "Write message and select expert"
                    : "Submit message"
              }
              title={
                isPending
                  ? "Cancel message"
                  : isSubmitDisabled
                    ? "Write message and select expert"
                    : "Submit message"
              }
            >
              <FontAwesomeIcon icon={isPending ? faStop : faArrowUp} />
            </Button>
          </TooltipTrigger>
        </Tooltip>
      )}
    </Row>
  );
};
